import { useCallback, useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "../../../lib/supabase";
import {
  fetchVoicemailAudioUrl,
  fetchVoicemails,
  hideVoicemail,
  markVoicemailListened,
} from "../voiceApi";
import type { VoiceCall } from "../types";
import { extractDigits, formatPhone } from "../phone";

const OUTCOME_BADGE: Record<string, string> = {
  voicemail: "bg-red-600 text-white",
  "voicemail-heard": "bg-gray-600 text-gray-300",
  forwarded: "bg-green-700 text-white",
  missed: "bg-gray-600 text-gray-300",
  "in-progress": "bg-yellow-600 text-white",
};

function timeAgo(dateStr: string): string {
  try {
    return `${formatDistanceToNow(new Date(dateStr))} ago`;
  } catch {
    return "";
  }
}

type Props = {
  phoneLine: "line1" | "line2" | null;
};

export function VoicemailsView({ phoneLine }: Props) {
  const [voicemails, setVoicemails] = useState<VoiceCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Lazily-loaded authed audio blob URLs, keyed by voicemail id. We don't
  // prefetch — the audio proxy only fires when the user clicks play.
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [audioLoading, setAudioLoading] = useState<Record<string, boolean>>({});
  const [audioError, setAudioError] = useState<Record<string, string>>({});

  // Track which ids were already marked listened so we don't PATCH twice
  // during the same session (the user can scrub through an audio clip).
  const listenedMarked = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Default inbox = voicemail + voicemail-heard. The worker already
      // applies that default when no `outcome` param is sent — don't echo
      // it here so we don't re-trip the double-wrap bug on stale workers.
      const rows = await fetchVoicemails({
        limit: 100,
        phoneLine,
      });
      setVoicemails(rows);
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [phoneLine]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime — post-cutover (0003) the tables live in `voice.voice_calls`.
  // Realtime's `postgres_changes` filter routes by (schema, table), both of
  // which must match the publication — so the schema field DOES matter.
  //
  // We listen on BOTH `INSERT` and `UPDATE`:
  //   - INSERT: new voicemail rows drop into the top of the inbox.
  //   - UPDATE: the async Deepgram transcription path PATCHes `transcript`
  //     ~15–75s after `voicemail-complete` fires. Without UPDATE, the UI stays
  //     at "Transcription pending…" until the user hits Refresh. With it, the
  //     text flips in place the moment the DB write lands.
  useEffect(() => {
    const channel = supabase
      .channel("hq-voice-voicemails")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "voice", table: "voice_calls" },
        (payload) => {
          const row = payload.new as VoiceCall;
          if (row.outcome !== "voicemail" && row.outcome !== "voicemail-heard") return;
          if (phoneLine && row.phone_line !== phoneLine) return;
          setVoicemails((prev) => {
            if (prev.some((v) => v.id === row.id)) return prev;
            return [row, ...prev];
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "voice", table: "voice_calls" },
        (payload) => {
          const row = payload.new as VoiceCall;
          // If an UPDATE flips a row to `hidden=true` (archive), pull it out
          // of the list the same way handleHide() already does optimistically.
          if (row.hidden) {
            setVoicemails((prev) => prev.filter((v) => v.id !== row.id));
            return;
          }
          // Mirror the INSERT filter so a row that transitions INTO voicemail
          // outcomes (e.g. `in-progress → voicemail` on voicemail-complete, if
          // we ever miss the INSERT) still shows up, and rows that transition
          // OUT (e.g. `voicemail → forwarded` — rare, but possible) drop out.
          const isVmOutcome =
            row.outcome === "voicemail" || row.outcome === "voicemail-heard";
          if (!isVmOutcome) {
            setVoicemails((prev) => prev.filter((v) => v.id !== row.id));
            return;
          }
          if (phoneLine && row.phone_line !== phoneLine) return;
          setVoicemails((prev) => {
            const idx = prev.findIndex((v) => v.id === row.id);
            if (idx === -1) return [row, ...prev];
            const next = prev.slice();
            next[idx] = { ...next[idx], ...row };
            return next;
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [phoneLine]);

  // Revoke the blob URLs we created when the component unmounts or rows change,
  // so we don't leak memory after a long session with lots of voicemails.
  useEffect(() => {
    return () => {
      Object.values(audioUrls).forEach((u) => {
        if (u.startsWith("blob:")) URL.revokeObjectURL(u);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLoadAudio(call: VoiceCall) {
    if (audioUrls[call.id] || audioLoading[call.id]) return;
    setAudioLoading((m) => ({ ...m, [call.id]: true }));
    setAudioError((m) => {
      const n = { ...m };
      delete n[call.id];
      return n;
    });
    try {
      const url = await fetchVoicemailAudioUrl(call.id);
      setAudioUrls((m) => ({ ...m, [call.id]: url }));
    } catch (err) {
      setAudioError((m) => ({ ...m, [call.id]: (err as Error).message }));
    } finally {
      setAudioLoading((m) => {
        const n = { ...m };
        delete n[call.id];
        return n;
      });
    }
  }

  async function handlePlay(call: VoiceCall) {
    if (call.outcome !== "voicemail") return;
    if (listenedMarked.current.has(call.id)) return;
    listenedMarked.current.add(call.id);
    try {
      await markVoicemailListened(call.id);
      setVoicemails((prev) =>
        prev.map((v) => (v.id === call.id ? { ...v, outcome: "voicemail-heard" } : v))
      );
    } catch (err) {
      console.error("mark listened failed", err);
    }
  }

  async function handleHide(call: VoiceCall) {
    try {
      await hideVoicemail(call.id);
      setVoicemails((prev) => prev.filter((v) => v.id !== call.id));
      const url = audioUrls[call.id];
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    } catch (err) {
      console.error("hide voicemail failed", err);
    }
  }

  function handleCopy(call: VoiceCall) {
    const digits = extractDigits(call.caller_number);
    navigator.clipboard
      .writeText(digits)
      .then(() => {
        setCopiedId(call.id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {});
  }

  const unreadCount = voicemails.filter((v) => v.outcome === "voicemail").length;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <h2 className="text-base font-semibold">Voicemails</h2>
        {unreadCount > 0 && (
          <span className="rounded-full bg-tribal-red px-2 py-0.5 text-[10px] font-bold text-white">
            {unreadCount}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {loadError && <span className="text-xs text-red-400">{loadError}</span>}
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-tribal-muted">
            Loading voicemails…
          </div>
        ) : voicemails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-tribal-muted">
            <div className="mb-3 text-5xl" aria-hidden>
              📭
            </div>
            <p className="text-sm">No voicemails in view.</p>
          </div>
        ) : (
          voicemails.map((call) => {
            const isUnread = call.outcome === "voicemail";
            const audioUrl = audioUrls[call.id];
            const isLoadingAudio = !!audioLoading[call.id];
            const err = audioError[call.id];
            const badge = OUTCOME_BADGE[call.outcome] ?? "bg-gray-600 text-gray-300";

            return (
              <div
                key={call.id}
                className={`mb-3 rounded-lg bg-tribal-surface p-4 ${
                  isUnread ? "border-l-4 border-tribal-red" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm ${isUnread ? "font-bold text-white" : "text-white"}`}
                    >
                      {formatPhone(call.caller_number)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(call)}
                      title="Copy number"
                      className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
                    >
                      {copiedId === call.id ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${badge}`}
                    >
                      {(call.outcome ?? "unknown").replace("-", " ")}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleHide(call)}
                      title="Archive (hide from view)"
                      aria-label="Archive voicemail"
                      className="rounded px-1.5 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="mt-1 text-sm text-tribal-muted">
                  {timeAgo(call.created_at)}
                  {call.recording_duration && call.recording_duration > 0
                    ? ` · ${call.recording_duration}s`
                    : ""}
                </div>

                {call.recording_url ? (
                  audioUrl ? (
                    <audio
                      controls
                      src={audioUrl}
                      onPlay={() => handlePlay(call)}
                      className="mt-3 w-full"
                    />
                  ) : (
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isLoadingAudio}
                        onClick={() => handleLoadAudio(call)}
                        className="rounded-md bg-tribal-red px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
                      >
                        {isLoadingAudio ? "Loading…" : "▶ Load audio"}
                      </button>
                      {err && <span className="text-xs text-red-400">{err}</span>}
                    </div>
                  )
                ) : null}

                {call.transcript ? (
                  <p className="mt-2 text-sm italic text-tribal-muted">"{call.transcript}"</p>
                ) : isUnread || call.outcome === "voicemail-heard" ? (
                  <p className="mt-2 text-sm italic text-tribal-muted/60">Transcription pending…</p>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
